import type {
  ApiErrorBody,
  AudiencePreviewRequest,
  AudiencePreviewResponse,
  BulkDeleteContactsResponse,
  CampaignResponse,
  CampaignRunResponse,
  Channel,
  ConsentRecordResponse,
  ContactResponse,
  CreateCampaignRequest,
  CreateContactRequest,
  CreateListRequest,
  CreateSegmentRequest,
  ImportPreviewRequest,
  ImportPreviewResponse,
  ImportStatusResponse,
  LaunchRequest,
  LaunchResponse,
  ListResponse,
  OpsHealth,
  Paginated,
  ReadinessResponse,
  RecordConsentRequest,
  ResubscribeRequest,
  RunReport,
  RunCohort,
  CohortKind,
  ContactTimeline,
  SegmentResponse,
  SessionResponse,
  WhatsappCatalog,
  TagResponse,
  UpdateCampaignRequest,
  UpdateContactRequest,
} from '@outreach/contracts';

/** Client-side view of the API error envelope (SPEC §9). */
export class ApiRequestError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly status: number,
    readonly fieldErrors?: Record<string, string[]>,
  ) {
    super(message);
    this.name = 'ApiRequestError';
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  // Only declare a JSON content-type when there is a body; a bodyless POST with
  // an application/json header is rejected as an empty JSON body.
  const headers: Record<string, string> = { ...((init?.headers as Record<string, string>) ?? {}) };
  if (init?.body !== undefined && init?.body !== null) {
    headers['Content-Type'] = 'application/json';
  }

  let res: Response;
  try {
    res = await fetch(path, {
      credentials: 'include',
      ...init,
      headers,
    });
  } catch {
    throw new ApiRequestError('network_error', 'Could not reach the server.', 0);
  }

  const text = await res.text();
  const body = text ? (JSON.parse(text) as unknown) : undefined;

  if (!res.ok) {
    const envelope = body as ApiErrorBody | undefined;
    throw new ApiRequestError(
      envelope?.error?.code ?? 'error',
      envelope?.error?.message ?? res.statusText,
      res.status,
      envelope?.error?.fieldErrors,
    );
  }
  return body as T;
}

function jsonBody(value: unknown): RequestInit {
  return { method: 'POST', body: JSON.stringify(value) };
}

export interface ContactListParams {
  page?: number;
  pageSize?: number;
  q?: string;
}

export const api = {
  session: () => request<SessionResponse>('/api/auth/session'),
  login: (password: string) =>
    request<{ authenticated: true }>('/api/auth/login', jsonBody({ password })),
  logout: () => request<SessionResponse>('/api/auth/logout', { method: 'POST' }),
  readiness: () => request<ReadinessResponse>('/api/health/ready'),

  contacts: {
    list: (params: ContactListParams = {}) => {
      const qs = new URLSearchParams();
      if (params.page) qs.set('page', String(params.page));
      if (params.pageSize) qs.set('pageSize', String(params.pageSize));
      if (params.q) qs.set('q', params.q);
      const query = qs.toString();
      return request<Paginated<ContactResponse>>(`/api/contacts${query ? `?${query}` : ''}`);
    },
    get: (id: string) => request<ContactResponse>(`/api/contacts/${id}`),
    timeline: (id: string) => request<ContactTimeline>(`/api/contacts/${id}/timeline`),
    create: (body: CreateContactRequest) =>
      request<ContactResponse>('/api/contacts', jsonBody(body)),
    update: (id: string, body: UpdateContactRequest) =>
      request<ContactResponse>(`/api/contacts/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    remove: (id: string, mode: 'archive' | 'erase') =>
      request<ContactResponse>(`/api/contacts/${id}?mode=${mode}`, { method: 'DELETE' }),
    /** Delete the ticked ids, or every live contact matching the search (`all`). */
    bulkDelete: (
      target: { ids: string[] } | { all: true; q?: string },
      mode: 'archive' | 'erase',
    ) =>
      request<BulkDeleteContactsResponse>(
        '/api/contacts/bulk-delete',
        jsonBody({ ...target, mode }),
      ),
    consentHistory: (id: string) =>
      request<{ items: ConsentRecordResponse[] }>(`/api/contacts/${id}/consent`),
    recordConsent: (id: string, body: RecordConsentRequest) =>
      request<ConsentRecordResponse>(`/api/contacts/${id}/consent`, jsonBody(body)),
    resubscribe: (id: string, body: ResubscribeRequest) =>
      request<ConsentRecordResponse>(`/api/contacts/${id}/resubscribe`, jsonBody(body)),
    withdraw: (id: string, channel: Channel) =>
      request<ConsentRecordResponse>(`/api/contacts/${id}/withdraw`, jsonBody({ channel })),
    suppress: (id: string, channel: Channel) =>
      request<ContactResponse>(`/api/contacts/${id}/suppress`, jsonBody({ channel })),
  },

  tags: {
    list: () => request<{ items: TagResponse[] }>('/api/tags'),
    create: (name: string) => request<TagResponse>('/api/tags', jsonBody({ name })),
    remove: (id: string) => request<{ ok: true }>(`/api/tags/${id}`, { method: 'DELETE' }),
  },

  lists: {
    list: () => request<{ items: ListResponse[] }>('/api/lists'),
    create: (body: CreateListRequest) => request<ListResponse>('/api/lists', jsonBody(body)),
    remove: (id: string) => request<{ ok: true }>(`/api/lists/${id}`, { method: 'DELETE' }),
  },

  segments: {
    list: () => request<{ items: SegmentResponse[] }>('/api/segments'),
    create: (body: CreateSegmentRequest) =>
      request<SegmentResponse>('/api/segments', jsonBody(body)),
    remove: (id: string) => request<{ ok: true }>(`/api/segments/${id}`, { method: 'DELETE' }),
  },

  audience: {
    preview: (body: AudiencePreviewRequest) =>
      request<AudiencePreviewResponse>('/api/audience/preview', jsonBody(body)),
  },

  imports: {
    preview: (body: ImportPreviewRequest) =>
      request<ImportPreviewResponse>('/api/imports/preview', jsonBody(body)),
    commit: (id: string, idempotencyKey: string) =>
      request<ImportStatusResponse>(`/api/imports/${id}/commit`, {
        method: 'POST',
        headers: { 'Idempotency-Key': idempotencyKey },
        body: JSON.stringify({}),
      }),
    status: (id: string) => request<ImportStatusResponse>(`/api/imports/${id}`),
    cancel: (id: string) =>
      request<ImportStatusResponse>(`/api/imports/${id}/cancel`, {
        method: 'POST',
        body: JSON.stringify({}),
      }),
    errorsUrl: (id: string) => `/api/imports/${id}/errors`,
  },

  campaigns: {
    list: () => request<{ items: CampaignResponse[] }>('/api/campaigns'),
    get: (id: string) => request<CampaignResponse>(`/api/campaigns/${id}`),
    create: (body: CreateCampaignRequest) =>
      request<CampaignResponse>('/api/campaigns', jsonBody(body)),
    update: (id: string, body: UpdateCampaignRequest) =>
      request<CampaignResponse>(`/api/campaigns/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    clone: (id: string) =>
      request<CampaignResponse>(`/api/campaigns/${id}/clone`, { method: 'POST' }),
    archive: (id: string) =>
      request<CampaignResponse>(`/api/campaigns/${id}/archive`, { method: 'POST' }),
    run: (id: string) => request<CampaignRunResponse>(`/api/campaigns/${id}/run`),
    report: (id: string) => request<RunReport>(`/api/campaigns/${id}/report`),
    cohort: (id: string, kind: CohortKind) =>
      request<RunCohort>(`/api/campaigns/${id}/cohort?kind=${kind}`),
    cohortCsvUrl: (id: string, kind: CohortKind) => `/api/campaigns/${id}/cohort?kind=${kind}&format=csv`,
    launch: (id: string, body: LaunchRequest) =>
      request<LaunchResponse>(`/api/campaigns/${id}/launch`, jsonBody(body)),
    pause: (id: string) =>
      request<CampaignResponse>(`/api/campaigns/${id}/pause`, { method: 'POST' }),
    resume: (id: string) =>
      request<CampaignResponse>(`/api/campaigns/${id}/resume`, { method: 'POST' }),
    cancel: (id: string) =>
      request<CampaignResponse>(`/api/campaigns/${id}/cancel`, { method: 'POST' }),
  },

  ops: {
    health: () => request<OpsHealth>('/api/ops/health'),
    pause: (paused: boolean) => request<OpsHealth>('/api/ops/pause', jsonBody({ paused })),
    pauseChannel: (channel: string, paused: boolean) =>
      request<OpsHealth>(`/api/ops/channels/${channel}/pause`, jsonBody({ paused })),
  },

  whatsapp: {
    templates: () =>
      request<WhatsappCatalog | { syncedAt: null; templates: [] }>('/api/whatsapp/templates'),
    sync: () => request<WhatsappCatalog>('/api/whatsapp/templates/sync', { method: 'POST' }),
  },

  /** Export/error downloads use a plain authenticated GET (cookie sent). */
  exportUrl: (q?: string) => `/api/export${q ? `?${q}` : ''}`,
};
